import { TrendingUp, Award, Users, Target } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { useState, useEffect } from 'react';

const StatsSection = () => {
  const { ref, isVisible } = useScrollReveal();

  const stats = [
    {
      icon: Award,
      value: '10+',
      label: 'Projects Completed',
      description: 'Successful campaigns delivered',
    },
    {
      icon: Target,
      value: '200%',
      label: 'Average Traffic Growth',
      description: 'Organic visibility improvement',
    },
    {
      icon: Users,
      value: '95%',
      label: 'Client Satisfaction',
      description: 'Happy clients worldwide',
    },
    {
      icon: TrendingUp,
      value: '150+',
      label: 'Keywords Ranked',
      description: 'First page rankings achieved',
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center space-y-3 p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 transition-all duration-500 hover:shadow-[var(--shadow-card)] hover:scale-105 ${
                isVisible ? 'animate-fade-in opacity-100' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                <stat.icon className="w-7 h-7 text-primary" />
              </div>
              <AnimatedCounter value={stat.value} isVisible={isVisible} />
              <div className="text-base md:text-lg font-semibold text-foreground">
                {stat.label}
              </div>
              <p className="text-sm text-muted-foreground">
                {stat.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AnimatedCounter = ({ value, isVisible }: { value: string; isVisible: boolean }) => {
  const [count, setCount] = useState(0);
  const targetNumber = parseInt(value.replace(/[^0-9]/g, ''));
  const suffix = value.replace(/[0-9]/g, '');

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const duration = 2000;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * targetNumber));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, targetNumber]);

  return (
    <div className="text-4xl md:text-5xl font-bold gradient-text">
      {count}{suffix}
    </div>
  );
};

export default StatsSection;
